import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MapPin, User, Loader2, Grid3X3, Calendar } from 'lucide-react';
import { getHearts, getStats, getFileUrl } from '@/lib/api';
import { cityConfig } from '@/config/cities';

const HeartCard = ({ heart, onClick, index }) => {
  const photoUrl = getFileUrl(heart.close_photo_path);
  const contextPhotoUrl = getFileUrl(heart.context_photo_path);
  const discoveredDate = heart.discovered_at 
    ? new Date(heart.discovered_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    : null;
  const discoveredTime = heart.discovered_at
    ? new Date(heart.discovered_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      className="card-gallery cursor-pointer"
      data-testid={`heart-card-${heart.heart_number}`}
    >
      {/* Photos */}
      <div className="mb-4">
        {/* Close-up photo */}
        <div className="aspect-square w-full bg-black overflow-hidden relative">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={`Coeur #${heart.heart_number}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className="w-12 h-12 text-[#E63946]/30" />
            </div>
          )}
          {/* Number overlay */}
          <div className="absolute top-2 left-2 bg-[#0A0A0B]/80 px-2 py-1">
            <span className="font-bold text-[#E63946]">#{heart.heart_number}</span>
          </div>
        </div>
        {/* Context photo (smaller strip below) */}
        {contextPhotoUrl && (
          <div className="h-20 w-full bg-black overflow-hidden mt-1">
            <img
              src={contextPhotoUrl}
              alt={`Contexte #${heart.heart_number}`}
              className="w-full h-full object-cover opacity-80"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-white">
          <User className="w-4 h-4 text-[#A1A1AA]" />
          <span className="font-medium truncate">{heart.pseudo}</span>
        </div>
        {heart.location_name && (
          <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{heart.location_name}</span>
          </div>
        )}
        {discoveredDate && (
          <div className="flex items-center gap-2 text-[#71717A] text-xs">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span>{discoveredDate} à {discoveredTime}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Gallery = () => {
  const navigate = useNavigate();
  const [hearts, setHearts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heartsData, statsData] = await Promise.all([
          getHearts(),
          getStats()
        ]);
        setHearts(heartsData);
        setStats(statsData);
      } catch (e) {
        console.error('Failed to fetch gallery data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center" data-testid="gallery-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-10rem)] px-6 py-8" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-3">
            <Grid3X3 className="w-6 h-6 text-[#E63946]" />
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase">
              Galerie
            </h1>
          </div>
          {stats && (
            <div className="text-right">
              <div className="text-2xl font-display font-bold text-[#E63946]">
                {stats.total_discovered}
              </div>
              <div className="label-style">/ {cityConfig.totalHearts} trouvés</div>
            </div>
          )}
        </motion.div>

        {/* Progress Bar */}
        {stats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="progress-bar">
              <div 
                className="progress-bar-fill"
                style={{ width: `${(stats.total_discovered / cityConfig.totalHearts) * 100}%` }}
              />
            </div>
            <p className="text-center text-[#A1A1AA] text-sm mt-2">
              {stats.remaining} cœurs encore à découvrir
            </p>
          </motion.div>
        )}

        {/* Empty State */}
        {hearts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Heart className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
            <h2 className="text-xl font-display font-bold mb-2">Aucun cœur découvert</h2>
            <p className="text-[#A1A1AA] mb-6">Sois le premier Finder !</p>
            <button
              onClick={() => navigate('/find')}
              className="btn-primary"
              data-testid="find-first-heart-button"
            >
              Enregistrer un cœur
            </button>
          </motion.div>
        ) : (
          /* Hearts Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {hearts.map((heart, index) => (
              <HeartCard
                key={heart.id}
                heart={heart}
                index={index}
                onClick={() => navigate(`/heart/${heart.heart_number}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
